import { Search } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useAdminSearch } from "../../api/admin";
import { AdminSearchResult } from "../../types/admin";
import { SEARCH_INPUT_PROPS } from "./inputs";

const TYPE_LABELS: Record<AdminSearchResult["type"], string> = {
  user: "Utilisateur",
  book: "Livre",
  subscription: "Commande",
  anecdote: "Anecdote",
};

const resultPath = (result: AdminSearchResult) =>
  result.type === "book"
    ? `/admin/books/${result.id}`
    : `/admin/users/${result.user_id}`;

/** Sidebar search over emails, names, book ids/titles, Shopify orders and tokens. */
export const AdminSearch = () => {
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const { data, isFetching } = useAdminSearch(debounced);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(q.trim()), 250);
    return () => clearTimeout(timer);
  }, [q]);

  useEffect(() => {
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const results = debounced.length >= 2 ? (data?.results ?? []) : [];

  return (
    <div ref={ref} className="relative px-3 pt-4">
      <div className="relative">
        <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          {...SEARCH_INPUT_PROPS}
          placeholder="Rechercher un user, un livre, une commande…"
          className="w-full border border-gray-200 rounded-md pl-8 pr-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>
      {open && debounced.length >= 2 && (
        <div className="absolute left-3 z-20 mt-1 w-[28rem] max-h-96 overflow-y-auto bg-white border border-gray-200 rounded-md shadow-lg">
          {results.length === 0 ? (
            <p className="px-3 py-2 text-sm text-gray-400">
              {isFetching ? "Recherche…" : "Aucun résultat"}
            </p>
          ) : (
            results.map((result) => (
              <button
                key={`${result.type}-${result.id}`}
                onClick={() => {
                  navigate(resultPath(result));
                  setOpen(false);
                  setQ("");
                }}
                className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-50 last:border-0"
              >
                <p className="text-sm text-gray-900">
                  <span className="text-xs text-gray-400 mr-2">
                    {TYPE_LABELS[result.type]}
                  </span>
                  {result.label}
                </p>
                {result.hint && (
                  <p className="text-xs text-gray-500">{result.hint}</p>
                )}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
